/* WIPO Publishing Contracts admin panel — pick a book, pick a template,
 * fill fields, e-sign natively (SHA-256 sealed), and export PDF.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, ShieldCheck, PenTool, RefreshCw, Loader2, Save } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function WipoContractsPanel() {
  const [books, setBooks] = useState([]);
  const [bookId, setBookId] = useState('');
  const [bookIds, setBookIds] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [fields, setFields] = useState({});
  const [contracts, setContracts] = useState([]);
  const [active, setActive] = useState(null);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get('/wipo/templates').then((r) => setTemplates(r.data.templates || []));
    http.get('/admin/bookstore/books').then((r) => setBooks(r.data?.books || []));
  }, []);

  const loadContracts = async (bid) => {
    if (!bid) return;
    const r = await http.get(`/admin/wipo/contracts/${bid}`);
    setContracts(r.data.contracts || []);
    setActive(r.data.contracts?.[0] || null);
  };
  useEffect(() => { loadContracts(bookId); }, [bookId]);

  const tpl = templates.find((t) => t.id === templateId);

  const createContract = async () => {
    const chosen = bookIds.length > 0 ? bookIds : (bookId ? [bookId] : []);
    if (!chosen.length || !templateId) return toast.error('Pick book(s) and a template first.');
    setBusy(true);
    try {
      const r = await http.post('/admin/wipo/contracts',
        chosen.length > 1
          ? { book_ids: chosen, template_id: templateId, fields }
          : { book_id: chosen[0], template_id: templateId, fields });
      toast.success(`Contract created${chosen.length > 1 ? ` covering ${chosen.length} books` : ''}.`);
      setFields({});
      await loadContracts(chosen[0]);
      setActive(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Create failed.'); }
    finally { setBusy(false); }
  };

  const sign = async (role) => {
    if (!active) return;
    const name = prompt(`Signer name (${role}):`);
    if (!name) return;
    try {
      const r = await http.post(`/admin/wipo/contracts/id/${active.id}/sign`,
        { signer_role: role, signer_name: name, consent: true });
      toast.success(`Signed as ${role}. Status: ${r.data.status}`);
      await loadContracts(bookId);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Sign failed.'); }
  };

  const openPdf = () => {
    if (!active) return;
    window.open(`${API}/admin/wipo/contracts/id/${active.id}/pdf`, '_blank');
  };

  return (
    <div className="space-y-4" data-testid="admin-wipo-panel">
      <div>
        <h2 className="flex items-center gap-2 text-[16px] font-semibold tracking-tight">
          <FileText className="h-4 w-4 text-primary" /> WIPO Publishing Contracts
        </h2>
        <p className="mt-0.5 text-[11.5px] text-muted-foreground">
          Native e-sign with SHA-256 tamper seal. Three WIPO Model
          Publishing Contract templates (Author-Publisher, Translator,
          Distribution).
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-md border border-border bg-card p-3">
          <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">Create new</div>
          <label className="block text-[11px] font-semibold">Book(s) — pick one or Ctrl-click multiple</label>
          <select
            multiple
            className="mt-1 h-32 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
            value={bookId ? [bookId] : []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
              setBookId(selected[0] || '');
              setBookIds(selected);
            }}
            data-testid="wipo-book-select"
          >
            {books.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}
          </select>
          <label className="mt-2 block text-[11px] font-semibold">Template</label>
          <select
            className="mt-1 h-8 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
            value={templateId}
            onChange={(e) => { setTemplateId(e.target.value); setFields({}); }}
            data-testid="wipo-template-select"
          >
            <option value="">—</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
          {tpl && (
            <div className="mt-2 grid gap-1.5">
              {tpl.fields.map((f) => (
                <label key={f} className="block">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{f}</div>
                  <input
                    className="mt-0.5 h-7 w-full rounded-sm border border-border bg-background px-1.5 text-[11.5px]"
                    value={fields[f] || ''}
                    onChange={(e) => setFields({ ...fields, [f]: e.target.value })}
                    data-testid={`wipo-field-${f}`}
                  />
                </label>
              ))}
              <button
                type="button" onClick={createContract} disabled={busy}
                data-testid="wipo-create-btn"
                className="mt-2 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90"
              >
                <Save className="h-4 w-4" /> Create draft
              </button>
            </div>
          )}
        </section>

        <section className="rounded-md border border-border bg-card p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Contracts on this book</div>
            <button
              type="button" onClick={() => loadContracts(bookId)}
              data-testid="wipo-refresh"
              className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[10.5px] hover:bg-secondary"
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          </div>
          {contracts.length === 0 && (
            <div className="text-[12px] text-muted-foreground">No contracts on this book yet.</div>
          )}
          {contracts.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActive(c)}
              data-testid={`wipo-contract-${c.id}`}
              className={`mt-1 block w-full rounded-sm border px-2 py-1.5 text-left text-[11.5px] transition ${
                active?.id === c.id ? 'border-primary bg-primary/10' : 'border-border bg-background hover:bg-secondary'
              }`}
            >
              <div className="font-semibold">{c.template_title}</div>
              <div className="text-[10.5px] text-muted-foreground">
                {c.status} · {c.signatures?.length || 0} signature(s) · {c.created_at?.slice(0, 10)}
              </div>
            </button>
          ))}
          {active && (
            <div className="mt-3 space-y-2 rounded-md border border-border bg-panel-header/40 p-3">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Selected</div>
              {active.book_ids && active.book_ids.length > 1 && (
                <div className="text-[10.5px] text-muted-foreground" data-testid="wipo-multibook-label">
                  Covers {active.book_ids.length} books
                </div>
              )}
              <div className="font-mono text-[10.5px] break-all">SHA-256: {active.content_sha256}</div>
              <div className="flex flex-wrap gap-1.5">
                {['author', 'publisher', 'translator', 'distributor'].map((role) => {
                  const signed = (active.signatures || []).find((s) => s.signer_role === role);
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => sign(role)}
                      disabled={!!signed}
                      data-testid={`wipo-sign-${role}`}
                      className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[10.5px] ${
                        signed ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'border-border bg-background hover:bg-secondary'
                      }`}
                    >
                      <PenTool className="h-3 w-3" /> {role} {signed ? '✓' : ''}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={openPdf}
                        data-testid="wipo-download-pdf"
                        className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary">
                  <FileText className="h-3 w-3" /> Download signed PDF
                </button>
                <button
                  type="button"
                  data-testid="wipo-share-link"
                  onClick={async () => {
                    try {
                      const r = await http.post(`/admin/wipo/contracts/id/${active.id}/share-link`, { expires_hours: 72 });
                      const base = (process.env.REACT_APP_BACKEND_URL || '') + r.data.pdf_url;
                      await navigator.clipboard.writeText(base);
                      toast.success('Share link (72 h) copied.');
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Share failed.'); }
                  }}
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary"
                >
                  <FileText className="h-3 w-3" /> Share link
                </button>
                <button
                  type="button"
                  data-testid="wipo-audit-open"
                  onClick={async () => {
                    try {
                      const r = await http.get(`/admin/wipo/contracts/id/${active.id}/audit`);
                      setAudit(r.data);
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Audit failed.'); }
                  }}
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary"
                >
                  <ShieldCheck className="h-3 w-3" /> Audit trail
                </button>
                <a
                  href={`${API}/wipo/contracts/${active.id}/verify`}
                  target="_blank" rel="noreferrer"
                  data-testid="wipo-verify-link"
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary"
                >
                  <ShieldCheck className="h-3 w-3" /> Verify seal
                </a>
              </div>
              {audit && (
                <div className="mt-2 max-h-96 overflow-y-auto rounded-sm border border-border bg-background p-2" data-testid="wipo-audit-view">
                  {audit.books?.length > 0 && (
                    <div className="mb-3">
                      <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                        Books covered ({audit.books.length})
                      </div>
                      <div className="flex flex-wrap gap-2" data-testid="wipo-audit-bundle">
                        {audit.books.map((b) => (
                          <div key={b.id} className="w-24 rounded-sm border border-border bg-panel-header/40 p-1 text-center">
                            {b.cover_url ? (
                              <img src={b.cover_url} alt={b.title}
                                     className="mx-auto h-24 w-16 rounded-sm object-cover" />
                            ) : (
                              <div className="mx-auto flex h-24 w-16 items-center justify-center rounded-sm bg-slate-500/20 text-[9px] text-muted-foreground">
                                no cover
                              </div>
                            )}
                            <div className="mt-1 truncate text-[9.5px] font-semibold" title={b.title}>{b.title}</div>
                            <div className="truncate text-[9px] text-muted-foreground">{b.author || '—'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">Signatures</div>
                  <table className="w-full text-[10.5px]">
                    <thead><tr className="text-left text-muted-foreground"><th>Role</th><th>Name</th><th>IP</th><th>OTP</th><th>Signed at</th></tr></thead>
                    <tbody>
                      {audit.audits.map((a, i) => (
                        <tr key={i} className="border-b border-border/60">
                          <td className="pr-1 font-semibold">{a.signer_role}</td>
                          <td className="pr-1">{a.signer_name}</td>
                          <td className="pr-1 font-mono">{a.ip}</td>
                          <td className="pr-1">{a.otp_verified ? '✓' : '—'}</td>
                          <td>{a.signed_at?.slice(0, 19).replace('T', ' ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-2 mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">OTPs issued</div>
                  <table className="w-full text-[10.5px]">
                    <thead><tr className="text-left text-muted-foreground"><th>Role</th><th>Email</th><th>Issued</th><th>Consumed</th></tr></thead>
                    <tbody>
                      {audit.otps.map((o, i) => (
                        <tr key={i} className="border-b border-border/60">
                          <td className="pr-1 font-semibold">{o.signer_role}</td>
                          <td className="pr-1 truncate max-w-[16ch]">{o.signer_email || '—'}</td>
                          <td className="pr-1">{o.issued_at?.slice(0, 19).replace('T', ' ')}</td>
                          <td>{o.consumed ? o.consumed_at?.slice(0, 19).replace('T', ' ') : (o.expires_at < new Date().toISOString() ? 'expired' : 'pending')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
